import React from 'react';
import { Box, Typography, makeStyles, Chip } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  statusPill: {
    height: 26,
    borderRadius: 999,
    padding: '0 10px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
  },
  statusCount: {
    fontSize: 11,
    fontWeight: 700,
  },
  statusLabel: {
    fontSize: 10,
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
  doneStatus: {
    backgroundColor: 'rgba(34, 197, 94, 0.08)',
    border: '1px solid rgba(34, 197, 94, 0.14)',
    '& $statusCount': {
      color: '#22C55E',
    },
  },
  inProgressStatus: {
    backgroundColor: 'rgba(245, 158, 11, 0.08)',
    border: '1px solid rgba(245, 158, 11, 0.14)',
    '& $statusCount': {
      color: '#F59E0B',
    },
  },
  todoStatus: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(107, 107, 122, 0.08)'
        : 'rgba(107, 107, 122, 0.12)',
    border: '1px solid rgba(107, 107, 122, 0.14)',
    '& $statusCount': {
      color: theme.palette.text.secondary,
    },
  },
}));

type StatusPillProps = {
  count: number;
  label: string;
  status: 'done' | 'in_progress' | 'todo';
};

export const StatusPill = ({ count, label, status }: StatusPillProps) => {
  const classes = useStyles();

  const statusClass = {
    done: classes.doneStatus,
    in_progress: classes.inProgressStatus,
    todo: classes.todoStatus,
  }[status];

  return (
    <Box className={`${classes.statusPill} ${statusClass}`}>
      <Typography className={classes.statusCount}>{count}</Typography>
      <Typography className={classes.statusLabel}>{label}</Typography>
    </Box>
  );
};

// Project card types
const projectCardStyles = makeStyles(theme => ({
  projectItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 12px',
    gap: 10,
    borderRadius: 8,
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  projectIcon: {
    width: 24,
    height: 24,
    borderRadius: 6,
    backgroundColor: 'rgba(168, 85, 247, 0.09)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  projectInfo: {
    flex: 1,
    minWidth: 0,
  },
  projectName: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  projectMeta: {
    fontSize: 10,
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  projectStats: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  statChip: {
    height: 18,
    fontSize: 10,
    fontWeight: 500,
    borderRadius: 4,
  },
  doneChip: {
    backgroundColor: 'rgba(34, 197, 94, 0.12)',
    color: '#22C55E',
  },
  progressChip: {
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    color: '#F59E0B',
  },
  todoChip: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? 'rgba(107, 107, 122, 0.12)'
        : 'rgba(107, 107, 122, 0.18)',
    color: theme.palette.text.secondary,
  },
  time: {
    fontSize: 10,
    color: theme.palette.text.secondary,
    flexShrink: 0,
    minWidth: 40,
    textAlign: 'right',
  },
}));

type ProjectItemProps = {
  name: string;
  client?: string;
  href?: string;
  icon?: React.ReactNode;
  stats?: {
    done?: number;
    inProgress?: number;
    todo?: number;
  };
  time?: string;
  onClick?: () => void;
};

export const ProjectItem = ({
  name,
  client,
  href,
  icon,
  stats,
  time,
  onClick,
}: ProjectItemProps) => {
  const classes = projectCardStyles();

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  const content = (
    <>
      {icon && <Box className={classes.projectIcon}>{icon}</Box>}
      <Box className={classes.projectInfo}>
        <Typography className={classes.projectName}>{name}</Typography>
        {client && (
          <Typography className={classes.projectMeta}>{client}</Typography>
        )}
      </Box>
      {stats && (
        <Box className={classes.projectStats}>
          {stats.done !== undefined && stats.done > 0 && (
            <Chip
              label={stats.done}
              size="small"
              className={`${classes.statChip} ${classes.doneChip}`}
            />
          )}
          {stats.inProgress !== undefined && stats.inProgress > 0 && (
            <Chip
              label={stats.inProgress}
              size="small"
              className={`${classes.statChip} ${classes.progressChip}`}
            />
          )}
          {stats.todo !== undefined && stats.todo > 0 && (
            <Chip
              label={stats.todo}
              size="small"
              className={`${classes.statChip} ${classes.todoChip}`}
            />
          )}
        </Box>
      )}
      {time && <Typography className={classes.time}>{time}</Typography>}
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        className={classes.projectItem}
        onClick={handleClick}
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        {content}
      </a>
    );
  }

  return (
    <Box className={classes.projectItem} onClick={handleClick}>
      {content}
    </Box>
  );
};
